/**
 * LoginScreen - Email input for passwordless login
 * Following new Figma design with light lavender theme
 *
 * Simple wireframe UI - easy to replace later
 * User enters email → receives OTP code
 *
 * All styling is configurable in config/loginConfig.ts
 */

import { SUPPORT_EMAIL, CREATOR_NAME, LOGIN_SUBTITLE } from '@/config/brandConfig'
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth'
import { ErrorModal } from '@/components/ui/ErrorModal'
import { loginText } from '@/config/loginConfig'

// Google Identity Services types
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
            auto_select?: boolean
          }) => void
          renderButton: (
            element: HTMLElement,
            config: {
              theme?: 'outline' | 'filled_blue' | 'filled_black'
              size?: 'large' | 'medium' | 'small'
              text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin'
              shape?: 'rectangular' | 'pill' | 'circle' | 'square'
              width?: number
            }
          ) => void
          prompt: () => void
        }
      }
    }
  }
}

interface LoginScreenProps {
  onCodeSent: (email: string) => void
}

export function LoginScreen({ onCodeSent }: LoginScreenProps) {
  const navigate = useNavigate()
  const { requestOTP, loginWithGoogle, error, clearError, isLoading } = useAuth()
  const [email, setEmail] = useState('')
  const [localError, setLocalError] = useState('')
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>('')
  const [inviteToken, setInviteToken] = useState<string | null>(null)
  const [googleLoading, setGoogleLoading] = useState(false)

  // Google Sign-In configuration
  const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID

  // Handle Google Sign-In callback
  const handleGoogleCallback = useCallback(async (response: { credential: string }) => {
    try {
      setGoogleLoading(true)
      setLocalError('')
      clearError()

      const result = await loginWithGoogle(response.credential)

      if (result.isApplicant) {
        // Applicant flow - redirect to apply page
        navigate('/apply')
      } else {
        // Check if user came from /vip and redirect back there
        const params = new URLSearchParams(window.location.search)
        const returnTo = params.get('returnTo')
        if (returnTo === '/vip' || returnTo === '/status') {
          navigate(returnTo, { replace: true })
        } else if (result.needsWelcome) {
          navigate('/welcome')
        } else {
          navigate('/')
        }
      }
    } catch (err) {
      console.error('Google Sign-In error:', err)
      setLocalError(err instanceof Error ? err.message : 'Google Sign-In failed')
    } finally {
      setGoogleLoading(false)
    }
  }, [loginWithGoogle, navigate, clearError])

  // Initialize Google Sign-In
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      return
    }

    // Wait for Google Identity Services script to load
    const initializeGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
        })

        // Render the Google Sign-In button
        const buttonContainer = document.getElementById('google-signin-button')
        if (buttonContainer) {
          window.google.accounts.id.renderButton(buttonContainer, {
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            width: 280,
          })
        }
      }
    }

    // Check if script is already loaded
    if (window.google?.accounts?.id) {
      initializeGoogle()
    } else {
      // Wait for script to load
      const checkGoogle = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogle)
          initializeGoogle()
        }
      }, 100)

      // Cleanup
      return () => clearInterval(checkGoogle)
    }
  }, [GOOGLE_CLIENT_ID, handleGoogleCallback, showLoginForm])

  // Check for invite token in URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('invite')
    const inviteEmail = params.get('email')

    if (token) {
      setInviteToken(token)
      setShowLoginForm(true) // Auto-show login form for invited users

      // Pre-fill email if provided
      if (inviteEmail) {
        setEmail(decodeURIComponent(inviteEmail))
      }
    } else if (inviteEmail) {
      // Came from an invite link (/join): that email is already approved, so just log in
      setShowLoginForm(true)
      setEmail(inviteEmail)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError('')
    clearError()

    // Validate email
    if (!email || !email.includes('@')) {
      setLocalError(loginText.emailValidationError)
      return
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

      // ---- INVITE PATH 1: If user has invite token from URL ----
      if (inviteToken) {
        const inviteResponse = await fetch(`${API_URL}/api/subscriptions/invites/${inviteToken}`)

        if (!inviteResponse.ok) {
          setErrorMessage('This invite link is invalid or has expired. Please contact support.')
          setShowErrorModal(true)
          return
        }

        const { invite } = await inviteResponse.json()

        // Verify email matches invite
        if (invite.email.toLowerCase() !== email.toLowerCase()) {
          setErrorMessage('Please use the email address this invite was sent to.')
          setLocalError('Email does not match invite')
          return
        }

        // Verify invite is still pending
        if (invite.status !== 'pending') {
          setErrorMessage('This invite has already been claimed or has expired.')
          setShowErrorModal(true)
          return
        }

        // Store invite token for later use in subscription creation
        localStorage.setItem('pending_invite_token', inviteToken)

        // BYPASS eligibility check and proceed directly to OTP
        await requestOTP(email)
        onCodeSent(email)
        return
      }

      // ---- STEP 1: Check if user already has a profile (returning user) ----
      try {
        const profileCheckResponse = await fetch(`${API_URL}/api/auth/check-profile?email=${encodeURIComponent(email)}`)

        if (profileCheckResponse.ok) {
          const profileCheckData = await profileCheckResponse.json()

          if (profileCheckData.hasProfile) {
            // User exists, proceed directly to OTP (normal login)
            await requestOTP(email)
            onCodeSent(email)
            return
          }
        }
      } catch (profileCheckError) {
        // no-op
      }

      // ---- STEP 2: Check if email has pending invite (new user with invite) ----
      try {
        // Check if this email has a pending invite
        const inviteCheckResponse = await fetch(`${API_URL}/api/subscriptions/invites/check-by-email?email=${encodeURIComponent(email)}`)

        if (inviteCheckResponse.ok) {
          const inviteCheckData = await inviteCheckResponse.json()

          if (inviteCheckData.hasInvite && inviteCheckData.invite) {
            // Store invite token for later use
            localStorage.setItem('pending_invite_token', inviteCheckData.invite.token)

            // BYPASS eligibility check and proceed directly to OTP
            await requestOTP(email)
            onCodeSent(email)
            return
          }
        }
      } catch (inviteCheckError) {
        // no-op
      }

      // ---- STEP 3: Send OTP ----
      // With the applicant system, ALL emails get OTP sent
      // - Existing profiles → normal login
      // - Pending invites → invite flow
      // - Stripe customers → normal login
      // - Unknown emails → applicant flow (new!)
      // The backend handles all cases and returns isApplicant flag if needed
      await requestOTP(email)
      // Move to code verification screen
      onCodeSent(email)
    } catch (err) {
      // Error is already set in context
      console.error('Failed to send code:', err)
    }
  }

  return (
    <>
      {/* Error Modal for non-existing emails */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Invite Not Found"
        message={errorMessage}
        buttonText="Try Again"
        secondaryAction={{
          label: "Apply for Access",
          onClick: () => {
            setShowErrorModal(false)
            navigate('/apply')
          }
        }}
      />

      <div className="relative min-h-screen forest-login-bg flex flex-col">
        <div className="relative z-10 flex flex-col min-h-screen w-full max-w-md mx-auto px-7 py-10">
          {/* ── Wordmark (upper-middle) ── */}
          <div className="pt-24 text-center animate-fade-in">
            <h1 className="font-script font-bold text-braydin-ink leading-[0.95] text-[4.75rem] sm:text-[6.75rem] sm:tracking-wide drop-shadow-[0_1px_12px_rgba(238,234,224,0.7)]">
              {CREATOR_NAME}
            </h1>
            <div className="mt-2 font-heading font-semibold text-base sm:text-xl tracking-[0.5em] sm:tracking-[0.72em] pl-[0.5em] sm:pl-[0.72em] text-braydin-oliveDark">
              WRIGHT
            </div>
            <p className="mt-5 font-heading text-[11px] tracking-[0.36em] pl-[0.36em] text-braydin-ink/70">
              {LOGIN_SUBTITLE}
            </p>
          </div>

          {/* spacer above the actions (ENTER sits just under the wordmark) */}
          <div className="flex-[0.32]" />

          {/* ── Actions (centered) ── */}
          <div className="space-y-5">
            {!showLoginForm ? (
              <div className="space-y-5 animate-slide-up">
                <button
                  onClick={() => setShowLoginForm(true)}
                  className="w-full py-4 rounded-xl bg-braydin-olive hover:bg-braydin-oliveDark text-braydin-cream font-heading font-semibold tracking-[0.28em] pl-[0.28em] text-sm btn-hover transition-smooth shadow-lg"
                >
                  ENTER
                </button>
                <p className="text-center text-[11px] leading-relaxed tracking-[0.08em] text-white/75">
                  BY LOGGING IN YOU AGREE TO THE{' '}
                  <button onClick={() => navigate('/terms')} className="underline underline-offset-2 text-braydin-sage hover:text-braydin-cream transition-colors">TERMS OF SERVICE</button>
                  {' '}AND{' '}
                  <button onClick={() => navigate('/privacy')} className="underline underline-offset-2 text-braydin-sage hover:text-braydin-cream transition-colors">PRIVACY POLICY</button>
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in bg-braydin-ink/40 backdrop-blur-md rounded-2xl p-5 border border-white/10 shadow-xl">
                <div>
                  <label htmlFor="email" className="block text-xs font-medium tracking-wide text-white/80 mb-1.5">
                    {loginText.emailLabel}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={loginText.emailPlaceholder}
                    disabled={isLoading}
                    className="w-full px-4 py-3 rounded-xl bg-braydin-cream/95 text-braydin-ink placeholder-braydin-taupe border border-white/20 focus:outline-none focus:ring-2 focus:ring-braydin-olive"
                  />
                </div>

                {(localError || error) && (
                  <div className="text-red-300 text-sm animate-fade-in">{localError || error}</div>
                )}

                <button
                  type="submit"
                  disabled={isLoading || googleLoading}
                  className="w-full py-3.5 rounded-xl bg-braydin-olive hover:bg-braydin-oliveDark text-braydin-cream font-heading font-semibold tracking-[0.2em] pl-[0.2em] text-sm btn-hover disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
                >
                  {isLoading ? loginText.submitButtonLoading : loginText.submitButton}
                </button>

                {GOOGLE_CLIENT_ID && (
                  <>
                    <div className="relative my-3">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/20"></div>
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="px-2 bg-transparent text-white/60">or</span>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div id="google-signin-button" className={googleLoading ? 'opacity-50 pointer-events-none' : ''}></div>
                    </div>
                    {googleLoading && (
                      <p className="text-center text-sm text-white/60 mt-2">Signing in with Google...</p>
                    )}
                  </>
                )}

                <button
                  type="button"
                  onClick={() => setShowLoginForm(false)}
                  className="w-full py-2 text-white/70 hover:text-white text-sm transition-smooth"
                >
                  {loginText.backButton}
                </button>
              </form>
            )}
          </div>

          {/* spacer below the actions (keeps SUPPORT lifted off the very bottom) */}
          <div className="flex-[0.4]" />

          {/* ── Support ── */}
          <div className="text-center pb-6">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-2.5 pl-7 pr-5 py-2.5 text-[11px] font-heading tracking-[0.22em] leading-none text-white/90 border border-white/40 hover:border-white/70 hover:bg-white/10 rounded-full transition-all"
              >
                <svg className="w-4 h-4 shrink-0 -translate-y-[1px]" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5a9 9 0 0 1 18 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3" />
                </svg>
                SUPPORT
              </a>
            </div>
        </div>
      </div>
    </>
  )
}
